import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Truck, Package, Shield, Clock, RefreshCw, CreditCard, Heart, CheckCircle2, Phone } from 'lucide-react';

export default function ServicesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      icon: Truck,
      title: "Free Local Delivery",
      description: "We bring prescriptions and health essentials directly to your door across all five NYC boroughs.",
      features: ["All 5 NYC Boroughs", "Same-day available", "Priority Red Hook"],
      color: "blue",
      link: "/delivery"
    },
    {
      icon: Package,
      title: "Blister Packaging",
      description: "Custom medication packaging organized by day and time. Perfect for multiple medications.",
      features: ["Organized by day/time", "Easy to use", "Reduces errors"],
      color: "green"
    },
    {
      icon: RefreshCw,
      title: "Medication Sync",
      description: "We sync all your prescriptions to refill on the same day each month.",
      features: ["All meds on one day", "Auto reminders", "Never run out"],
      color: "purple"
    },
    {
      icon: Shield,
      title: "OTC Benefit Cards",
      description: "We accept over-the-counter benefit cards from most insurance plans.",
      features: ["Most plans accepted", "Easy checkout", "Expert guidance"],
      color: "teal"
    },
    {
      icon: CreditCard,
      title: "NY Medicaid (eMedNY)",
      description: "Full assistance with New York Medicaid prescriptions and prior authorizations.",
      features: ["eMedNY expertise", "Prior auth help", "Coverage navigation"],
      color: "orange"
    },
    {
      icon: Heart,
      title: "Medicare Navigation",
      description: "We help you understand your plan and maximize your Medicare benefits.",
      features: ["Plan comparison", "Coverage questions", "Part D expertise"],
      color: "red"
    }
  ];

  const colorClasses = {
    blue: { bg: "bg-blue-50", icon: "text-blue-600" },
    green: { bg: "bg-green-50", icon: "text-green-600" },
    purple: { bg: "bg-purple-50", icon: "text-purple-600" },
    teal: { bg: "bg-teal-50", icon: "text-teal-600" },
    orange: { bg: "bg-orange-50", icon: "text-orange-600" },
    red: { bg: "bg-red-50", icon: "text-red-600" }
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/50">
      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="container-custom text-center">
          <span className="text-sm font-semibold text-cyan-600 uppercase tracking-wider mb-4 block">Our Services</span>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Healthcare That <span className="text-slate-500">Works for You</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            From free delivery to expert payer advocacy, we make managing your health simple.
          </p>
        </div>
      </section>

      {/* Featured - Free Delivery */}
      <section className="container-custom mb-12">
        <div className="bg-slate-900 p-8 md:p-10 rounded-3xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-green-500 text-white px-3 py-1.5 rounded-full text-sm font-medium mb-4">
                <Truck className="w-4 h-4" />
                Most Popular
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">FREE Delivery Across NYC</h2>
              <p className="text-slate-300 mb-6">
                We bring your prescriptions directly to you, absolutely free across all five boroughs.
              </p>
              <Link to="/delivery" className="inline-flex items-center gap-2 bg-white text-slate-900 px-6 py-3 rounded-full font-medium hover:bg-slate-100 transition-colors">
                Request Delivery
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-white/10 p-5 rounded-xl text-center">
                <div className="text-2xl font-bold text-white">5</div>
                <div className="text-slate-300 text-sm">NYC Boroughs</div>
              </div>
              <div className="bg-white/10 p-5 rounded-xl text-center">
                <div className="text-2xl font-bold text-white">$0</div>
                <div className="text-slate-300 text-sm">Delivery Fee</div>
              </div>
              <div className="bg-white/10 p-5 rounded-xl text-center">
                <div className="text-2xl font-bold text-white">Same</div>
                <div className="text-slate-300 text-sm">Day Available</div>
              </div>
              <div className="bg-white/10 p-5 rounded-xl text-center">
                <div className="text-2xl font-bold text-white">No</div>
                <div className="text-slate-300 text-sm">Minimum Order</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Services */}
      <section className="py-12 bg-white/50">
        <div className="container-custom">
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 text-center mb-10">
            Complete Pharmacy Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((service, index) => {
              const colors = colorClasses[service.color];
              const Icon = service.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-2xl border border-slate-100 hover:shadow-lg hover:border-cyan-200 transition-all duration-300 hover:-translate-y-1">
                  <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={`w-6 h-6 ${colors.icon}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{service.title}</h3>
                  <p className="text-slate-600 text-sm mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  {service.link && (
                    <Link to={service.link} className={`inline-flex items-center gap-1 ${colors.icon} text-sm font-medium mt-4 hover:gap-2 transition-all`}>
                      Learn More <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hours */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-md mx-auto bg-white p-8 rounded-2xl shadow-sm">
            <h3 className="text-xl font-semibold text-slate-900 mb-6 text-center">Hours of Operation</h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">Monday - Friday</span>
                <span className="font-medium text-slate-900">9:00 AM - 7:00 PM</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">Saturday</span>
                <span className="font-medium text-slate-900">9:00 AM - 3:00 PM</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-600">Sunday</span>
                <span className="text-slate-400">Closed</span>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-100 text-center">
              <a href="tel:+17187970200" className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-full font-medium hover:bg-slate-800">
                <Phone className="w-4 h-4" />
                Call (718) 797-0200
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-slate-900">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Ready to Experience the Difference?</h2>
          <p className="text-slate-300 mb-6">Transfer your prescriptions today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/transfer" className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-full font-medium hover:opacity-90">
              Transfer Prescription
            </Link>
            <Link to="/refill" className="border border-slate-600 text-white px-6 py-3 rounded-full font-medium hover:bg-slate-800">
              Refill Prescription
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
